import {getCookie} from 'cookies-next'
import moment from 'moment-timezone'
import _ from 'lodash'
import type {CSSProperties} from 'react'
// Utils
import {
  accessBallotIsPublic,
  accessBallotDisplayType,
} from './ballot-access'
import slugify from './slugify'
// Types
import type {
  Ballot,
  ParentCategory,
  SubCategory,
  CategoryAd,
  SponsorshipAd,
  AdPackage,
} from '../types/ballot'

interface GroupSlugCollision {
  slug: string
  groupIds: string[]
  groupNames: string[]
}

interface QuestionSlugCollision {
  groupId: string
  groupName: string
  slug: string
  questionIds: string[]
  questionNames: string[]
}

interface BallotSlugDiagnostics {
  groupSlugCollisions: GroupSlugCollision[]
  questionSlugCollisions: QuestionSlugCollision[]
  hasIssues: boolean
}

export const calculateSlugDiagnosticsByBallot = (ballot: Ballot | null | undefined): BallotSlugDiagnostics => {
  const diagnostics: BallotSlugDiagnostics = {
    groupSlugCollisions: [],
    questionSlugCollisions: [],
    hasIssues: false,
  }
  if (!ballot?.parent_categories?.length) return diagnostics

  const groupSlugMap = new Map<string, ParentCategory[]>()

  ballot.parent_categories.forEach(group => {
    const groupSlug = slugify(group.parent_category_name)
    if (!groupSlug) return

    const groupedValues = groupSlugMap.get(groupSlug) || []
    groupedValues.push(group)
    groupSlugMap.set(groupSlug, groupedValues)

    const questions = group.sub_categories || []
    const questionSlugMap = new Map<string, SubCategory[]>()
    questions.forEach(question => {
      const questionSlug = slugify(question.name)
      if (!questionSlug) return

      const questionSlugValues = questionSlugMap.get(questionSlug) || []
      questionSlugValues.push(question)
      questionSlugMap.set(questionSlug, questionSlugValues)
    })

    questionSlugMap.forEach((matchedQuestions, slug) => {
      if (matchedQuestions.length <= 1) return
      diagnostics.questionSlugCollisions.push({
        groupId: group._id,
        groupName: group.parent_category_name,
        slug,
        questionIds: matchedQuestions.map(question => question._id),
        questionNames: matchedQuestions.map(question => question.name),
      })
    })
  })

  groupSlugMap.forEach((matchedGroups, slug) => {
    if (matchedGroups.length <= 1) return
    diagnostics.groupSlugCollisions.push({
      slug,
      groupIds: matchedGroups.map(group => group._id),
      groupNames: matchedGroups.map(group => group.parent_category_name),
    })
  })

  diagnostics.hasIssues = diagnostics.groupSlugCollisions.length > 0 || diagnostics.questionSlugCollisions.length > 0
  return diagnostics
}

export const calculateHasOnlyOneGroup = (ballot: Ballot | null | undefined): boolean => {
  if (!ballot) return false
  // NOTE: 'multiple' means single group and 'groupsandcategories' means multiple groups
  return ballot.parent_categories?.length === 1 || ballot?.ballot_setup === 'multiple'
}

export const calculateDisplayAdPoolByBallotAndQuestion = (
  ballot: Ballot | null | undefined, question: SubCategory | null | undefined,
): CategoryAd[] => {
  if (_.isEmpty(ballot)) return []

  const filterAdPool = (displayAd: CategoryAd | null | undefined): boolean => {
    if (!displayAd) return false
    let adIsOfCorrectType = false
    switch (displayAd.adType) {
    case 'DisplayAd':
      adIsOfCorrectType = Boolean(displayAd.mobile_image_path) && Boolean(displayAd.desktop_image_path)
      break
    case 'ProfileCard': {
      const profileAdsAreEnabled = ballot.general?.profile_ads_option_turn_on
      const adIsMarkedToBeShown = displayAd.profile_card_additional_info?.show_as_display_ad
      if (profileAdsAreEnabled && adIsMarkedToBeShown) {
        adIsOfCorrectType = true
      }
      break
    }
    case 'SponsorWrite-ins':
      break
    default:
      adIsOfCorrectType = false
    }
    const publishSettings = ballot.publish_settings
    const currentTimezone = (
      _.find(moment.tz.names(), timezoneName => timezoneName === publishSettings?.timezone)
      || moment.tz.guess()
    )
    const currentDateTime = moment.tz(moment(), currentTimezone)
    const adStartDate = displayAd.start_date && moment.tz(displayAd.start_date, currentTimezone)
    const adHasStarted = adStartDate ? adStartDate.isSameOrBefore(currentDateTime) : true
    return adIsOfCorrectType && adHasStarted
  }
  const allAds = question?.category_ads
  return _.isEmpty(allAds) ? [] : _.filter(allAds, filterAdPool)
}

export const calculateFooterAdPoolByBallotAndGroup = (
  ballot: Ballot | null | undefined, group: ParentCategory | null | undefined,
): SponsorshipAd[] | null => {
  if (_.isEmpty(group?.sponsorship_ads)) return null
  const publishSettings = ballot?.publish_settings
  const currentTimezone = (
    _.find(moment.tz.names(), timezoneName => timezoneName === publishSettings?.timezone)
    || moment.tz.guess()
  )
  const currentDateTime = moment.tz(moment(), currentTimezone)

  const footerAds = group!.sponsorship_ads
  const visibleFooterAds = _.filter(footerAds, footerAd => {
    const adExists = Boolean(footerAd)
    const adStartDate = footerAd?.start_date && moment.tz(footerAd.start_date, currentTimezone)
    const adHasStarted = adStartDate ? adStartDate.isSameOrBefore(currentDateTime) : true
    const adHasAllImages = Boolean(footerAd?.mobile_image_path) && Boolean(footerAd.image_path)
    return adExists && adHasAllImages && adHasStarted
  })
  return visibleFooterAds
}

export const calculateSponsorshipAdPoolByBallot = (
  ballot: Ballot | null | undefined, isQuestionShow: boolean = false,
): SponsorshipAd[] | null => {
  if (_.isEmpty(ballot?.sponsorship_ads)) return null
  const publishSettings = ballot?.publish_settings
  const currentTimezone = (
    _.find(moment.tz.names(), timezoneName => timezoneName === publishSettings?.timezone)
    || moment.tz.guess()
  )
  const currentDateTime = moment.tz(moment(), currentTimezone)

  const namedSponsorshipAds = _.filter(ballot?.sponsorship_ads, ad => Boolean(ad?.ad_name))
  const mappedSponsorshipAds = _.compact(_.map(namedSponsorshipAds, ad => {
    return isQuestionShow ? (ad?.include_votepage_logo ? ad : null) : ad
  }))
  const visibleSponsorshipAds = _.filter(mappedSponsorshipAds, (sponsorshipAd: SponsorshipAd) => {
    const adExists = Boolean(sponsorshipAd)
    const adStartDate = sponsorshipAd?.start_date && moment.tz(sponsorshipAd.start_date, currentTimezone)
    const adHasStarted = adStartDate ? adStartDate.isSameOrBefore(currentDateTime) : true
    return adExists && adHasStarted
  })
  return visibleSponsorshipAds
}

export const calculateFirstQuestionUrl = (ballot: Ballot | null | undefined): string | null => {
  if (!ballot) return null

  const onlyGroup = _.first(ballot.parent_categories)
  if (!onlyGroup) return null
  const onlyGroupSlug = slugify(onlyGroup.parent_category_name)
  const allQuestions = _.filter(onlyGroup.sub_categories, question => Boolean(question?.name))
  const sortedQuestions = (
    onlyGroup.sort_order === 'manual'
      ? allQuestions
      : allQuestions?.sort((a, b) => a?.name?.trim()?.localeCompare(b?.name?.trim()))
  )
  const firstQuestion = _.first(sortedQuestions)
  const firstQuestionSlug = slugify(firstQuestion?.name)
  return `/${onlyGroupSlug}/${firstQuestionSlug}`
}

export const calculateGroupIndexContainerStyle = (ballot: Ballot | null | undefined): CSSProperties => {
  const groupBackgroundImagePath = ballot?.general?.group_background_image
  let newGroupIndexContainerStyle: CSSProperties = {
    minHeight: '100%',
    backgroundSize: 'cover',
  }
  if (groupBackgroundImagePath) {
    newGroupIndexContainerStyle.backgroundImage = `url(${groupBackgroundImagePath})`
  }

  const ballotDisplayType = accessBallotDisplayType(ballot)
  const groupIndexIsVisible = (
    ballotDisplayType !== 'landing-page-only'
    && (accessBallotIsPublic(ballot) || getCookie('_uhecp') === 'true')
  )
  if (!groupIndexIsVisible) {
    newGroupIndexContainerStyle = {height: '100%'}
  }
  return newGroupIndexContainerStyle
}

export const calculateBallotPassword = (ballot: Ballot | null | undefined): string | undefined => {
  return ballot?.publish_settings?.privacy?.private?.password
}

export const calculateBallotAdPackages = (ballot: Ballot | null | undefined): AdPackage[] | undefined => {
  return ballot?.general?.ad_price_settings?.additional_info?.packages
}

// Group calculators
export const calculateGroupByBallotAndGroupSlug = (
  ballot: Ballot | null | undefined, groupSlug: string,
): ParentCategory | undefined => {
  const allGroups = ballot?.parent_categories || []
  if (!groupSlug) return

  const matchedGroups = allGroups.filter(group => slugify(group.parent_category_name) === groupSlug)
  if (matchedGroups.length !== 1) return
  return matchedGroups[0]
}
